import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';

import { Button } from './Button';
import { focusFirst } from './focus';
import { Cross } from './icons';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
  /** Keep the dialog open while a request is in flight. */
  busy?: boolean;
}

/**
 * A modal on the native <dialog> element: the browser traps focus and handles Escape;
 * clicking the backdrop closes it unless the dialog is busy. Focus is managed explicitly
 * because callers mount and unmount dialogs: on open the first field gets focus (not the
 * Close button), and on close focus returns to the element that opened it. The page is
 * inert while a modal is open, so the native dialog is closed before focus is handed back.
 *
 * Opening and closing are animated in CSS (`dialog-motion`) on the element itself, so the
 * close is native and immediate and `onClose` is reported at once. A caller that mounts the
 * dialog only while it is needed keeps it mounted, closed, for `DIALOG_LINGER_MS` through
 * `useLingering`, which is what lets the exit transition finish.
 */
function isInside(el: HTMLElement | null, x: number, y: number): boolean {
  const rect = el?.getBoundingClientRect();
  if (!rect) return true;
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  busy = false,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  /** The next `close` event is ours, not the browser's, and needs no handling. */
  const selfClose = useRef(false);
  const openedAt = useRef(0);
  const pressedOutside = useRef(false);
  const titleId = useId();
  const descriptionId = useId();

  const restoreFocus = useCallback(() => {
    const target = opener.current;
    opener.current = null;
    if (!target || !document.contains(target)) return;
    target.focus();
  }, []);

  /** Close the native dialog now and hand focus back; the CSS exit transition plays on its own. */
  const settle = useCallback(
    (el: HTMLDialogElement) => {
      if (el.open) {
        selfClose.current = true;
        el.close();
      }
      restoreFocus();
    },
    [restoreFocus],
  );

  /** Close from inside the dialog (Escape, backdrop, the × button): close, hand focus back, tell the caller. */
  const dismiss = useCallback(() => {
    if (busy) return;
    const el = ref.current;
    if (el) settle(el);
    onClose();
  }, [busy, onClose, settle]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement ? active : null;
      openedAt.current = performance.now();
      el.showModal();
      focusFirst(bodyRef.current);
    } else if (!open && el.open) {
      settle(el);
    }
  }, [open, settle]);

  // Unmounting while open (a caller closing by unmounting) must also hand focus back.
  useEffect(() => {
    const el = ref.current;
    return () => {
      if (el?.open) {
        selfClose.current = true;
        el.close();
      }
      restoreFocus();
    };
  }, [restoreFocus]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleCancel = (event: Event) => {
      event.preventDefault();
      dismiss();
    };
    // A close the dialog did not initiate (for example a form with method="dialog").
    const handleClose = () => {
      if (selfClose.current) {
        selfClose.current = false;
        return;
      }
      restoreFocus();
      if (open) onClose();
    };
    el.addEventListener('cancel', handleCancel);
    el.addEventListener('close', handleClose);
    return () => {
      el.removeEventListener('cancel', handleCancel);
      el.removeEventListener('close', handleClose);
    };
  }, [open, onClose, dismiss, restoreFocus]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      className={[
        'dialog-motion m-auto rounded-lg border border-line bg-surface text-ink shadow-pop p-0',
        'w-[calc(100vw-32px)]',
        size === 'lg' ? 'max-w-[760px]' : 'max-w-[560px]',
      ].join(' ')}
      style={{ overscrollBehavior: 'contain' }}
      onPointerDown={(event) => {
        pressedOutside.current = !isInside(ref.current, event.clientX, event.clientY);
      }}
      onClick={(event) => {
        // A backdrop click dismisses only when the press also began on the backdrop, and not in
        // the first moments after opening: the second click of a double-click on the opening
        // button lands on the backdrop exactly where the button was.
        if (performance.now() - openedAt.current < 400) return;
        if (!pressedOutside.current) return;
        if (!isInside(ref.current, event.clientX, event.clientY)) dismiss();
      }}
    >
      <div className="flex items-start gap-4 px-6 pt-5 pb-3">
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="text-heading font-medium">
            {title}
          </h2>
          {description ? (
            <p id={descriptionId} className="mt-1 text-body text-ink-2">
              {description}
            </p>
          ) : null}
        </div>
        {/* 28px tall against a 24px title line: pulled up 2px so the × centres on the title. */}
        <Button
          variant="ghost"
          size="sm"
          aria-label="Close"
          onClick={dismiss}
          disabled={busy}
          className="-mr-2 -mt-0.5 px-1.5"
        >
          <Cross size={16} />
        </Button>
      </div>
      <div ref={bodyRef} className="px-6 pb-5 max-h-[70vh] overflow-y-auto">
        {children}
      </div>
      {footer ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-6 py-3 bg-surface-2 rounded-b-lg">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}
